import { useState, useEffect, useContext, useMemo } from "react";
import { Box, Tooltip, TextField, InputAdornment, Collapse, FormControl, Select, MenuItem, Divider, ListSubheader, FormControlLabel, Checkbox } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import clsx from 'clsx';

import { formatCurrency } 	from "../../../common/Helper";
import config 				from "../../../common/Config";
import useFetch 			from "../../../api/api"
import PortfolioContext 	from "../context";

import Loading 				from "../../../components/Loading";
import CustomFooter 		from "./TabListFooter";
import ConfirmDialog 		from "../../../components/ConfirmDialog";

import DeleteIcon 			from '@mui/icons-material/RemoveCircle';
import AddCircleIcon 		from '@mui/icons-material/AddCircle';
import PaidIcon 			from '@mui/icons-material/Paid';

const validateTargetValue = (value) => {
	const parsed = Number(value);
	if (Number.isNaN(parsed)) return 'Número inválido';
	if (parsed > 100) return 'Precisa ser menor ou igual a 100';
	if (parsed < 0) return 'Precisa ser maior ou igual a 0';
	return '';
};

const TabList = ({ assetType, toolbarOpen }) => {
	
	const privateFetch 								= useFetch()

	const navigate 									= useNavigate();
	const { userViews, allFields, convertToDollar }	= useContext(PortfolioContext)

	const [columns, setColumns]						= useState()
	const [confirmOpen, setConfirmOpen]				= useState(false)
	const [confirmTicker, setConfirmTicker]			= useState()
	const [view, setView]							= useState(null)
	const [showAllocation, setShowAllocation]		= useState(true)
	const [showResults, setShowResults]				= useState(true)
	const [filterAction, setFilterAction]			= useState([])

	const queryClient 								= useQueryClient()

	const { systemViews, categoryDescriptions } = useMemo(() => {
		if (!allFields || allFields.length === 0) return { systemViews: [], categoryDescriptions: {} }
		const catMap = {}
		allFields.forEach(f => {
			if (f.category && !catMap[f.category]) {
				catMap[f.category] = { order: f.categoryOrder ?? 99, label: f.categoryDescription ?? f.category }
			}
		})
		const sorted = Object.keys(catMap).sort((a, b) => catMap[a].order - catMap[b].order)
		const descriptions = Object.fromEntries(sorted.map(k => [k, catMap[k].label]))
		return { systemViews: sorted, categoryDescriptions: descriptions }
	}, [allFields])

	const fetchData = async () => {
		const data = await privateFetch(config.apiUrl +'/api/portfolio/assets/'+ (assetType ? assetType : 'ACAO'))
		.then(results => results.json())
		.then(results => {
			let total = results.data.reduce((acc, c) => acc + parseFloat(c.position), 0)
			return results.data.map(c => ({
				...c,
				percentage	: total == 0 ? 0 : ((c.position / total) * 100).toFixed(2),
				targetDiff	: total == 0 ? 0 : ((c.position / total) * 100).toFixed(2) - parseFloat(c.target)
			}))
		})

		return data
	}

	const { data : portfolio = [], isLoading } = useQuery({
		queryKey		: ['portfolio_'+ assetType],
		queryFn			: fetchData,
		refetchInterval	: false,
	})

	const distributedPoints = useMemo(
		() => portfolio.reduce((acc, c) => acc + parseFloat(c.target), 0),
		[portfolio]
	)

	const { data : portfolioGuidance = [] } = useQuery({
		queryKey		: ['portfolioGuidance_'+ assetType],
		queryFn			: () => privateFetch(config.apiUrl +'/api/portfolio/guidance/'+ (assetType || 'ACAO'))
							.then(r => r.json())
							.then(r => r.data || []),
		refetchInterval	: false,
	})

	const guidanceMap = portfolioGuidance.reduce((acc, r) => { acc[r.ticker] = r; return acc }, {})

	const filteredPortfolio = filterAction.length === 0
		? portfolio
		: portfolio.filter(row => filterAction.includes(guidanceMap[row.ticker]?.action ?? ''))

	useEffect(() => {
		if (!userViews) return
		// Inicializa com a primeira view do usuário, ou primeira categoria de sistema
		if (view === null) {
			if (userViews.length > 0) setView(userViews[0].id)
			else if (systemViews.length > 0) setView(systemViews[0])
		}
	}, [userViews, systemViews])

	useEffect(() => {
		loadUserColumns()
	}, [userViews, portfolioGuidance, allFields, view, showAllocation, showResults])

	const buildColumn = (field) => ({
		field			: field.id,
		headerName		: field.label,
		sortComparator	: (v1, v2) => field.type === 'numeric' ? parseFloat(v1) - parseFloat(v2) : (v1 < v2 ? 1 : -1),
		valueFormatter	: (value, row) => {
			const useOriginalCurrency = (field.category === 'FINANCIAL' || field.category === 'VALUATION' || field.category === 'PORTFOLIO') && !convertToDollar
			const currency = useOriginalCurrency ? row.currency : 'USD'
			switch (field.unit) {
				case 'currency'		: return formatCurrency(value, 'SMALL', currency)
				case 'bigCurrency'	: return formatCurrency(value, 'BIG', currency)
				case 'intNumber'	: return Math.round(value)
				case 'percent'		: return parseFloat(value).toFixed(2) +'%'
				default				: return value
			}
		}
	})

	const loadUserColumns = async () => {
		// Colunas fixas e obrigatorias
		let userColumns	=	[{
			field				: 'action',
			headerName			: '',
			width				: 10,
			disableColumnMenu 	: true,
			sortable 			: false,
			align				: 'center',
			cellClassName		: "portfolio-grid-fixed-column",
			renderCell: (cellValues) => {
				return (
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, direction: 'row', width: '100%', height: '100%' }}>
						{cellValues.row.inPortfolio ? (
							<Tooltip title="Remover do portfolio">
								<DeleteIcon sx={{ fontSize: 15, color: 'red', cursor: 'pointer' }} onClick={() => handleRemoveTarget(cellValues.row.ticker)}/>
							</Tooltip>
						) : (
							<Tooltip title="Adicionar ao portfolio">
								<AddCircleIcon sx={{ fontSize: 15, color: 'green', cursor: 'pointer' }} onClick={() => handleAddTarget(cellValues.row.ticker)}/>
							</Tooltip>
						)}
						<Tooltip title="Adicionar movimentação">
							<PaidIcon
								sx={{ fontSize: 15, color: 'gray', cursor: 'pointer' }}
								onClick={() => navigate(`/transactions/${(assetType || 'acao').toLowerCase()}`, { state: { openTab: 1, initialTicker: cellValues.row.ticker } })}
							/>
						</Tooltip>
					</Box>
				)
			}
		},{
			field 				: 'ticker',
			headerName			: 'Ticker',
			sortComparator 		: (v1, v2) => v1 < v2 ? 1 : -1,
			renderCell : (c) => {
				return <Link to={`/company/${c.row.ticker}`} target='_blank'>{c.row.ticker}</Link>;
			},
			resizable			: false,
			width				: 75,
			disableColumnMenu 	: true,
			align				: 'left',
			headerAlign			: 'left',
			cellClassName		: "portfolio-grid-fixed-column"
		}, {
			field				: 'percentage',
			headerName			: '%',
			sortComparator 		: (v1, v2) => parseFloat(v1) - parseFloat(v2),
			valueFormatter		: (value) => parseFloat(value).toFixed(2) +'%',
			resizable			: false,
			width				: 75,
			disableColumnMenu 	: true,
			align				: 'left',
			headerAlign			: 'left',
			cellClassName		: "portfolio-grid-fixed-column"
		}, {
			field				: 'targetDiff',
			headerName			: 'Dif.',
			sortComparator 		: (v1, v2) => parseFloat(v1) - parseFloat(v2),
			valueFormatter		: (value) => parseFloat(value).toFixed(2) +'%',
			cellClassName		: (params) => { return clsx('portfolio-grid-fixed-column', { positive: params.value > 0, negative: params.value < 0}) },
			resizable			: false,
			width				: 75,
			disableColumnMenu 	: true,
			align				: 'left',
			headerAlign			: 'left',
		},
		{
			field				: 'target',
			headerName			: 'Alvo',
			sortComparator 		: (v1, v2) => parseFloat(v1) - parseFloat(v2),
			resizable			: false,
			width				: 110,
			renderCell			: (params) => (
				<EditableTargetCell
					value={Number(params.row.target ?? 0).toFixed(2)}
					ticker={params.row.ticker}
					percentage={params.row.percentage}
					onSave={handleTargetChange}
				/>
			),
			disableColumnMenu 	: true,
			align				: 'left',
			headerAlign			: 'center',
			cellClassName		: "portfolio-grid-fixed-column-editable"
		},
		{
			field			: 'reviewStatus',
			headerName		: 'Resultado',
			width			: 80,
			sortable		: false,
			disableColumnMenu	: true,
			cellClassName		: 'portfolio-grid-fixed-column',
			align				: 'center',
			headerAlign			: 'center',
			renderCell		: (cellValues) => {
				const PERIOD_LABELS = { FY: 'FY', Q1: '1T', Q2: '2T', Q3: '3T', Q4: '4T' }
				const status = guidanceMap[cellValues.row.ticker]
				if (!status || status.year == null) return null
				const periodStr = PERIOD_LABELS[status.period] ?? status.period
				const yearStr   = String(status.year).slice(-2)
				const label     = status.period === 'FY' ? `${periodStr} ${yearStr}` : `${periodStr}${yearStr}`
				const hasReview = status.hasReview === 1
				const tooltipContent = (
					<Box sx={{ maxWidth: 420, p: 0.5 }}>
						{status.rationalText ? (
							<>
								<Box sx={{ fontWeight: 'bold', mb: 0.5, fontSize: 12 }}>Tese</Box>
								<Box sx={{ fontSize: 12, whiteSpace: 'pre-wrap', mb: status.reviewText ? 1 : 0 }}>{status.rationalText}</Box>
							</>
						) : (
							<Box sx={{ fontSize: 12, color: 'text.secondary', mb: status.reviewText ? 1 : 0 }}>Sem tese de investimento.</Box>
						)}
						{hasReview && (
							<>
								<Box sx={{ fontWeight: 'bold', mb: 0.5, fontSize: 12 }}>Última análise ({label})</Box>
								<Box sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{status.reviewText}</Box>
							</>
						)}
						{!hasReview && (
							<Box sx={{ fontSize: 12, color: 'text.secondary' }}>Sem análise para o período.</Box>
						)}
					</Box>
				)
				return (
					<Tooltip
						title={tooltipContent}
						componentsProps={{ tooltip: { sx: { maxWidth: 440, bgcolor: 'background.paper', color: 'text.primary', boxShadow: 3, border: '1px solid', borderColor: 'divider' } } }}
					>
						<Box
							onClick={() => window.open(`/company/${cellValues.row.ticker}#reviews`, '_blank')}
							sx={{
								fontWeight: 'bold',
								color      : hasReview ? '#2e7d32' : '#c62828',
								cursor     : 'pointer',
								whiteSpace : 'nowrap'
							}}
							>
							{label}
						</Box>
					</Tooltip>
				)
			}
		},
		{
			field			: 'stockAction',
			headerName		: 'Decisão',
			width			: 115,
			sortable		: false,
			disableColumnMenu	: true,
			cellClassName		: 'portfolio-grid-fixed-column',
			align				: 'center',
			headerAlign			: 'center',
			renderCell		: (cellValues) => {
				const guidance = guidanceMap[cellValues.row.ticker]
				if (!guidance) return null
				return (
					<ActionSelect
						value={guidance.action ?? ''}
						onChange={(newAction) => {
							privateFetch(config.apiUrl + '/api/portfolio/action', {
								method	: 'PATCH',
								headers	: { 'content-type': 'application/json' },
								body	: JSON.stringify({ ticker: cellValues.row.ticker, action: newAction || null })
							}).then(() => {
								queryClient.setQueryData(['portfolioGuidance_' + assetType], old =>
									old.map(r => r.ticker === cellValues.row.ticker ? { ...r, action: newAction || null } : r)
								)
							})
						}}
					/>
				)
			}
		}]

		const userView = (userViews || []).find(v => v.id === view)
		if (userView) {
			// View personalizada: campos na ordem configurada pelo usuário
			const viewFields = (userView.portfolioViewFields || [])
				.sort((a, b) => a.fieldOrder - b.fieldOrder)
			userColumns = userColumns.concat(
				viewFields
					.filter(f => f.fieldId !== 'ticker' && f.fieldId !== 'target')
					.map(f => {
						const fieldDef = (allFields || []).find(af => af.id === f.fieldId)
						return fieldDef ? buildColumn(fieldDef) : null
					})
					.filter(Boolean)
			)
		} else {
			// View de sistema: todas as colunas disponíveis da categoria
			userColumns = userColumns.concat(
				(allFields || [])
					.filter(f => f.category === view && f.id !== 'ticker' && f.id !== 'target')
					.sort((a, b) => a.label.localeCompare(b.label))
					.map(f => buildColumn(f))
			)
		}

		if (!showAllocation) userColumns = userColumns.filter(c => !['percentage', 'targetDiff', 'target'].includes(c.field))
		if (!showResults)    userColumns = userColumns.filter(c => !['reviewStatus', 'stockAction'].includes(c.field))

		setColumns(userColumns)
	}

	const handleTargetChange = async (updated, original) => {
		let options = {
			method : 'POST',
			headers : {
				'content-type' : 'application/json; charset=utf-8',
			},
			body : `{ 
						"ticker" : "`+ updated.ticker +`", 
						"target" : "`+ updated.target +`"
					}`
		}

		const data = await privateFetch(config.apiUrl +"/api/portfolio/addTarget", options);

		if (data.status === 200) {
			queryClient.setQueryData(['portfolio_'+ assetType], oldPortfolio =>
				oldPortfolio.map(p => p.ticker !== updated.ticker ? p : {
					...p,
					target		: updated.target,
					targetDiff	: parseFloat(updated.percentage) - parseFloat(updated.target)
				})
			)
			return true
		} 
		else {
			return false
		}
	}


	const handleAddTarget = (ticker) => {
		let options = {
			method : "POST",
			headers : {
				"Content-Type" : "application/json"	
			},
			body : `{ 
				"ticker" : "`+ ticker +`", 
				"target" : "0"
			}`
			
		}
		
		privateFetch(config.apiUrl +"/api/portfolio/addTarget", options)
		.then(() => {
			queryClient.setQueryData(['portfolio_'+ assetType], oldPortfolio =>
				oldPortfolio.map(p => p.ticker !== ticker ? p : { ...p, inPortfolio: true })
			)
		});
	}

	const handleRemoveTarget = (ticker) => {
		setConfirmTicker(ticker)
		setConfirmOpen(true)
	}

	const removeTarget = () => {
		let options = {
			method : 'DELETE',
			headers : {
				'content-type' : 'application/json; charset=utf-8',
			}
		}

		privateFetch(config.apiUrl +"/api/portfolio/"+ confirmTicker, options)
		.then((data) => data.json())
		.then((data) => {
			queryClient.setQueryData(['portfolio_'+ assetType], oldPortfolio => {
				const item = oldPortfolio.find(p => p.ticker === confirmTicker)
				if (item?.quantity > 0) {
					return oldPortfolio.map(p => p.ticker !== confirmTicker ? p : { ...p, inPortfolio: false, target: 0 })
				}
				return oldPortfolio.filter(p => p.ticker !== confirmTicker)
			})

		})
	}

	if (isLoading || !columns) return <Box display={'flex'} height={'100%'} alignItems={'center'} width={'100%'}><Loading/></Box>

	return (
		<Box padding={1} sx={{ width: '99%', height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
			<ConfirmDialog
				title="Remover Ticker?"
				open={confirmOpen}
				setOpen={setConfirmOpen}
				onConfirm={() => removeTarget(confirmTicker)}
				>
				Tem certeza que deseja remover {confirmTicker} do portfolio?
			</ConfirmDialog>
			<Collapse in={toolbarOpen && (systemViews.length > 0 || (userViews || []).length > 0)} unmountOnExit>
				<Box pb={1} display="flex" alignItems="center" gap={2} flexWrap="wrap">
					<Box display="flex" alignItems="center" gap={1}>
					<Box sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Views</Box>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<Select
							value={view}
							onChange={(e) => setView(e.target.value)}
							sx={{ fontSize: '0.75rem' }}
							>
							<ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '28px', color: 'text.disabled' }}>
								Views de sistema
							</ListSubheader>
							{systemViews.map(cat => (
								<MenuItem key={cat} value={cat} sx={{ fontSize: '0.75rem' }}>
									{categoryDescriptions[cat] ?? cat}
								</MenuItem>
							))}
							<Divider />
							<ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '28px', color: 'text.disabled' }}>
								Minhas views
							</ListSubheader>
							{(userViews || []).length === 0 && (
								<MenuItem disabled sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
									Nenhuma view criada
								</MenuItem>
							)}
							{(userViews || []).map(v => (
								<MenuItem key={v.id} value={v.id} sx={{ fontSize: '0.75rem' }}>
									{v.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<Box sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Decisão</Box>
						<FormControl size="small" sx={{ minWidth: 120 }}>
							<Select
								multiple
								value={filterAction}
								onChange={(e) => setFilterAction(e.target.value)}
								renderValue={(selected) => selected.length === 0 ? 'Todas' : selected.map(v => ACTION_OPTIONS.find(o => o.value === v)?.label ?? v).join(', ')}
								displayEmpty
								sx={{ fontSize: '0.75rem' }}
							>
								{ACTION_OPTIONS.filter(o => o.value !== '').map(o => (
									<MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.75rem', py: 0.5 }}>
										<Checkbox size="small" checked={filterAction.includes(o.value)} sx={{ py: 0, color: ACTION_COLORS[o.value] }} />
										<Box component="span" sx={{ color: ACTION_COLORS[o.value], fontWeight: 'bold', fontSize: '0.75rem' }}>{o.label}</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					<FormControlLabel
						control={<Checkbox size="small" checked={showAllocation} onChange={(e) => setShowAllocation(e.target.checked)} />}
						label="Mostrar Campos de Alocação"
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
					/>
					<FormControlLabel
						control={<Checkbox size="small" checked={showResults} onChange={(e) => setShowResults(e.target.checked)} />}
						label="Mostrar Campos de Análise de Resultados"
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
					/>
				</Box>
			</Collapse>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<DataGrid
					sx={{
						width: '100%',
						minWidth: 300,
						height: '100%',
						'& .Mui-error': {
							backgroundColor: 'rgb(126,10,15, 0.1)',
							color: '#750f0f'
						},
						'& .portfolio-grid-fixed-column': {
							backgroundColor: '#eeefef', 
						},
						'& .portfolio-grid-fixed-column-editable': {
							backgroundColor: '#eeefef', 
							cursor : 'pointer'
						},					
						'& .portfolio-grid-fixed-column.negative': {
							color: '#FF0000', 
						},
						'& .portfolio-grid-fixed-column.positive': {
							color: '#008000',
						},
						'& .MuiDataGrid-selectedRowCount': {
							display: 'none'
						},
						'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
							outline: 'none'
						}
					}}
					columns={columns}
					rows={filteredPortfolio}
					getRowId={(row) => row.ticker} 
					initialState={{
						density: 'compact',
						pagination: { paginationModel: { pageSize: 50 } },
						sorting: { sortModel: [{ field: 'ticker', sort: 'desc' }] }
					}}
					pageSizeOptions={[20,50,100]}
					disableRowSelectionOnClick
					slots={{footer: CustomFooter}}
					slotProps={{
						footer : { distributedPoints, showAllocation }
					}}
				/>
			</Box>
		</Box>
	)
}

const EditableTargetCell = ({ value, ticker, percentage, onSave }) => {
	const [localValue, setLocalValue] = useState(value)
	const [error, setError] = useState('')

	useEffect(() => { setLocalValue(value) }, [value])

	const handleSave = () => {
		const msg = validateTargetValue(localValue)
		if (msg) { setError(msg); return }
		const parsed = Number(localValue)
		if (parsed.toFixed(2) !== value) {
			onSave({ ticker, target: parsed, percentage }, { ticker, target: Number(value) })
		}
	}

	return (
		<Box sx={{ width: '100%' }} onClick={e => e.stopPropagation()}>
			<Tooltip
				title={error || ''}
				placement="right"
				open={!!error}
				disableFocusListener
				disableHoverListener
				disableTouchListener
				componentsProps={{ tooltip: { sx: { bgcolor: '#dc0e0e', color: '#fff', fontSize: 12 } } }}
			>
				<TextField
					fullWidth
					size="small"
					value={localValue}
					onChange={(e) => { setLocalValue(e.target.value); setError('') }}
					onFocus={(e) => e.target.select()}
					onBlur={handleSave}
					onKeyDown={(e) => {
						if (e.key === 'Enter') handleSave()
						if (e.key === 'Escape') { setLocalValue(value); setError('') }
					}}
					error={!!error}
					InputProps={{
						endAdornment: <InputAdornment position="end">%</InputAdornment>,
						inputProps: { min: 0, max: 100, style: { textAlign: 'center', fontWeight: 700 } }
					}}
					sx={{
						'& .MuiInputBase-root': { height: 34, backgroundColor: '#eef7ff' },
						'& .MuiInputAdornment-root': { marginLeft: 0 },
						'& input': { paddingRight: 0 }
					}}
				/>
			</Tooltip>
		</Box>
	)
}

const ACTION_OPTIONS = [
	{ value: '',         label: '—'        },
	{ value: 'analisar', label: 'Analisar' },
	{ value: 'comprar',  label: 'Comprar'  },
	{ value: 'manter',   label: 'Stand-by' },
	{ value: 'vender',   label: 'Vender'   },
]

const ACTION_COLORS = {
	analisar : '#1565c0',
	comprar  : '#2e7d32',
	manter   : '#e65100',
	vender   : '#c62828',
}

const ActionSelect = ({ value, onChange }) => {
	const handleChange = (e) => {
		e.stopPropagation()
		onChange(e.target.value)
	}
	return (
		<Box onClick={(e) => e.stopPropagation()} sx={{ width: '100%', px: 0.5 }}>
			<Select
				value={value}
				onChange={handleChange}
				variant="standard"
				disableUnderline
				displayEmpty
				size="small"
				sx={{
					fontSize		: 12,
					fontWeight		: 'bold',
					color			: ACTION_COLORS[value] ?? 'text.secondary',
					width			: '100%',
					'& .MuiSelect-select' : { py: 0 }
				}}
			>
				{ACTION_OPTIONS.map(opt => (
					<MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>{opt.label}</MenuItem>
				))}
			</Select>
		</Box>
	)
}

export default TabList
